import React, {useCallback, useRef, useEffect, useContext, useState} from "react";
import {Upload, message, Carousel, Spin} from "antd";
import {InboxOutlined, DeleteOutlined, LeftOutlined, RightOutlined, LoadingOutlined} from "@ant-design/icons";
import {StyledButton} from "../components/StyledButton";
import {GenericModal} from "../components/GenericModal";
import {Images} from "../images/Images";
import {OrderFeedbackContext} from "./OrderFeedbackProvider";
import {MAX_FILE_SIZE_MB, RATING_ASPECT_MAP, RATING_KEYS} from "./consts.jsx";
import {HttpClient} from "../http/HttpClient";
import "./order-feedback.css";

const {Dragger} = Upload;

export const OrderFeedbackUploadPage = ({history, match}) => {
    const {orderId} = match?.params || {};
    const {formValues, onFormValueChange, order} = useContext(OrderFeedbackContext);
    const {rating, selectedOptions, comment, images, fileList} = formValues;
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [uploading, setUploading] = useState(false);
    const carouselRef = useRef(null);

    useEffect(() => {
        // If no rating, redirect back to rating page
        if (!rating && orderId && history) {
            history.replace(`/order/${orderId}/feedback`);
        }
    }, [history, orderId, rating]);

    const handleBeforeUpload = useCallback(file => {
        const isLt5M = file.size / 1024 / 1024 < MAX_FILE_SIZE_MB;
        if (!isLt5M) {
            message.error(`הקובץ חייב להיות קטן מ-${MAX_FILE_SIZE_MB}MB!`);
        }
        return isLt5M;
    }, []);

    const handleRemoveFile = useCallback(
        imageId => {
            const currentFiles = formValues.images || [];
            const filtered = currentFiles.filter(image => image.id !== imageId);
            // Move carousel to previous slide if we removed the current one
            if (carouselRef.current && filtered.length > 0) {
                const removedIndex = currentFiles.findIndex(image => image.id === imageId);
                const currentIndex = carouselRef.current?.innerSlider?.currentSlide || 0;
                if (removedIndex <= currentIndex && currentIndex > 0) {
                    carouselRef.current.prev();
                }
            }
            onFormValueChange("images", filtered);
        },
        [formValues.images, onFormValueChange]
    );

    const handleUpload = useCallback(
        info => {
            const {status, uid} = info.file;

            // Check if any files are currently uploading
            const isAnyUploading = info.fileList.some(file => file.status === "uploading");
            setUploading(isAnyUploading);

            // Update fileList from info.fileList
            onFormValueChange("fileList", info.fileList);

            if (status === "done") {
                message.success(`${info.file.response.originalFilename || info.file.name} file uploaded successfully.`);

                // Process all completed files from fileList to avoid missing any
                const completedFiles = info.fileList
                    .filter(file => file.status === "done" && file.response)
                    .map(file => ({
                        id: file.response.imageId,
                        name: file.response.originalFilename || file.name,
                        url: file.response.imageUrl,
                        type: file.type
                    }));

                // Get existing images and merge, avoiding duplicates
                const prevImages = images || [];
                const existingIds = new Set(prevImages.map(img => img.id));
                const newImages = completedFiles.filter(file => !existingIds.has(file.id));
                const updatedImages = [...prevImages, ...newImages];

                onFormValueChange("images", updatedImages);
            } else if (status === "error") {
                message.error(`${info.file.name} file upload failed.`);
            }
        },
        [onFormValueChange, images]
    );

    const handleSubmit = useCallback(async () => {
        if (submitting) return;

        setSubmitting(true);
        try {
            // Map frontend option IDs to backend enum values
            const ratingAspects = (selectedOptions || []).map(optionId => RATING_ASPECT_MAP[optionId]).filter(Boolean); // Remove undefined values

            // Map images to backend format {imageId, imageName, imageUrl}
            const imagesToSubmit = (images || [])
                .filter(file => file.id && file.name && file.url)
                .map(file => ({
                    imageId: file.id,
                    imageName: file.name,
                    imageUrl: file.url
                }));

            const ratingSubmission = {
                rating,
                ratingAspects,
                comment: comment,
                ...(imagesToSubmit.length > 0 && {images: imagesToSubmit}),
                submittedVia: "web"
            };

            const response = await HttpClient.post(`/api/orders/${orderId}/ratings/submit`, ratingSubmission);

            if (response.error) {
                message.error(response.error || "שגיאה בשליחת המשוב. אנא נסה שוב.");
                setSubmitting(false);
                return;
            }

            setShowSuccessModal(true);
        } catch (error) {
            console.error("Error submitting rating:", error);
            message.error("שגיאה בשליחת המשוב. אנא נסה שוב.");
            setSubmitting(false);
        }
    }, [orderId, rating, selectedOptions, comment, images, submitting]);

    const handleSuccessModalClose = useCallback(() => {
        setShowSuccessModal(false);
        history.push(`/events/${orderId}/orderV2`);
    }, [history, orderId]);

    const isGoodRating = rating >= RATING_KEYS.OK;

    return (
        <>
            <div className="order-feedback-section">
                {isGoodRating ? (
                    <>
                        <h2 className="order-feedback-section-title">תנו השראה ללקוחות נוספים</h2>
                        <p className="order-feedback-section-description">מספרים זה לא הכל נשמח לקבל טעימה מההזמנה.</p>
                        <p className="order-feedback-section-description">התמונות יוכלו להעניק השראה ללקוחות הבאים</p>
                    </>
                ) : (
                    <>
                        <h2 className="order-feedback-section-title">תודה על השיתוף</h2>
                        <p className="order-feedback-section-description">
                            במידה ויש לך תמונה של תיאור המקרה נשמח לקבל אותה.
                        </p>
                    </>
                )}
            </div>

            <div className="order-feedback-upload-section">
                <Spin spinning={uploading} tip="מעלה קובץ...">
                    <Dragger
                        disabled={!!order?.rating || uploading}
                        name="file"
                        accept=".png,.jpg,.jpeg,.mp4"
                        multiple={true}
                        action={"/api/upload/"}
                        fileList={fileList ?? []}
                        beforeUpload={handleBeforeUpload}
                        onChange={handleUpload}
                        showUploadList={false}
                        className="order-feedback-upload-dragger">
                        <InboxOutlined />
                        <p className="ant-upload-text">{uploading ? "מעלה קובץ..." : "העלאת תמונות וסרטונים"}</p>
                        <p className="ant-upload-hint">ניתן להעלות מספר קבצים</p>
                        <p className="ant-upload-hint">מגבלת העלאה עבור כל קובץ היא עד {MAX_FILE_SIZE_MB}MB</p>
                    </Dragger>
                </Spin>
            </div>

            {images.length > 0 && (
                <div className="order-feedback-gallery-carousel">
                    {images.length > 1 && (
                        <>
                            <button
                                className="order-feedback-carousel-button-right"
                                onClick={() => carouselRef.current?.next()}>
                                <RightOutlined />
                            </button>
                            <button
                                className="order-feedback-carousel-button-left"
                                onClick={() => carouselRef.current?.prev()}>
                                <LeftOutlined />
                            </button>
                        </>
                    )}
                    <Carousel
                        className="order-feedback-carousel-container"
                        variableWidth={true}
                        centerMode={false}
                        ref={carouselRef}
                        slidesToScroll={1}
                        infinite={false}
                        dots={false}
                        swipeToSlide={true}
                        vertical={false}>
                        {images.map((image, index) => {
                            const isVideo = image?.type.split("/")[0] === "video";
                            return (
                                <div key={image.id} className="order-feedback-carousel-item-wrapper">
                                    <div className="order-feedback-carousel-item-container">
                                        <div className="order-feedback-carousel-delete-icon">
                                            <DeleteOutlined onClick={() => handleRemoveFile(image.id)} />
                                        </div>
                                        {isVideo ? (
                                            <video
                                                className="order-feedback-carousel-item"
                                                autoPlay={true}
                                                loop={true}
                                                muted={true}>
                                                <source src={image?.url} type={image.type} />
                                            </video>
                                        ) : (
                                            <img
                                                alt="photo"
                                                src={image?.url}
                                                className="order-feedback-carousel-item"
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </Carousel>
                </div>
            )}

            {!order?.rating ? (
                <div className="order-feedback-actions">
                    <StyledButton onClick={handleSubmit} className="order-feedback-button" disabled={submitting}>
                        {submitting ? "שולח..." : "שליחת משוב"}
                    </StyledButton>
                </div>
            ) : null}

            <GenericModal
                visible={showSuccessModal}
                onClose={handleSuccessModalClose}
                title="תודה שהקדשת רגע ודירגת את ההזמנה"
                text={["אנחנו כאן כדי שכל הזמנה שלך תהיה מעולה.", "המשוב שלך התקבל, נעבור עליו כדי להשתפר לפעם הבאה💙"]}
                image={Images.noteImage}
                options={[
                    {
                        id: "thankYou",
                        text: "תודה",
                        onClick: handleSuccessModalClose
                    }
                ]}
                className="order-feedback-success-modal"
                closeable={false}
            />
        </>
    );
};
